import type { ReportTemplate } from '~/gql/reports/reports'
import { ReportFrequency } from '@zeus/index'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Label } from '~/components/ui/label'
import ParticipantPicker from '~/pages/backoffice/admin/sessions/components/ParticipantPicker'
import { useReportSchedule } from './use-report-schedule'

const SELECT = 'h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm'
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]
const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1)
const HOURS = Array.from({ length: 24 }, (_, i) => i)

type Props = { template: ReportTemplate, onSaved: () => void }

/** Fréquence, jour, heure et destinataires de l'envoi d'un modèle existant (#163). */
function ReportScheduleForm({ template, onSaved }: Props) {
  const { t, i18n } = useTranslation()
  const schedule = useReportSchedule(template, onSaved)
  const { form } = schedule
  const patch = (changes: Partial<typeof form>) => schedule.setForm(current => ({ ...current, ...changes }))
  const weekly = form.frequency === ReportFrequency.WEEKLY

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        void schedule.save()
      }}
    >
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" checked={form.enabled} onChange={e => patch({ enabled: e.target.checked })} />
        {t('reports.schedule.enabled')}
      </label>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="scheduleFrequency">{t('reports.schedule.frequency')}</Label>
          <select id="scheduleFrequency" className={SELECT} value={form.frequency} onChange={e => patch({ frequency: e.target.value as ReportFrequency })}>
            {Object.values(ReportFrequency).map(f => <option key={f} value={f}>{t(`reports.schedule.frequencies.${f}`)}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="scheduleDay">{t(weekly ? 'reports.schedule.weekday' : 'reports.schedule.monthDay')}</Label>
          {weekly
            ? (
                <select id="scheduleDay" className={SELECT} value={form.weekday} onChange={e => patch({ weekday: Number(e.target.value) })}>
                  {WEEKDAYS.map(d => <option key={d} value={d}>{t(`reports.schedule.weekdays.${d}`)}</option>)}
                </select>
              )
            : (
                <select id="scheduleDay" className={SELECT} value={form.monthDay} onChange={e => patch({ monthDay: Number(e.target.value) })}>
                  {MONTH_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="scheduleHour">{t('reports.schedule.hour')}</Label>
          <select id="scheduleHour" className={SELECT} value={form.hour} onChange={e => patch({ hour: Number(e.target.value) })}>
            {HOURS.map(h => <option key={h} value={h}>{t('reports.schedule.hourOption', { hour: h })}</option>)}
          </select>
        </div>
      </div>
      <ParticipantPicker
        users={schedule.recipients}
        value={new Set(form.recipientIds)}
        onChange={next => patch({ recipientIds: [...next] })}
        labelKey="reports.schedule.recipients"
        searchLabelKey="reports.schedule.recipientsSearch"
      />
      {/* Le scalaire n'est décodé qu'au typage : Apollo rend la chaîne ISO telle quelle. */}
      {template.schedule?.lastSentAt && (
        <p className="text-xs text-muted-foreground">
          {t('reports.schedule.lastSent', { date: new Date(String(template.schedule.lastSentAt)).toLocaleString(i18n.language) })}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {template.schedule && (
          <Button type="button" variant="outline" disabled={schedule.saving} onClick={() => void schedule.stop()}>
            {t('reports.schedule.stop')}
          </Button>
        )}
        <Button type="submit" disabled={!schedule.canSave || schedule.saving}>{t('reports.schedule.save')}</Button>
      </div>
      {!schedule.canSave && <p className="text-xs text-muted-foreground">{t('reports.schedule.recipientsRequired')}</p>}
    </form>
  )
}

export default ReportScheduleForm
